import { Box, HStack, Text, useDisclosure, VStack } from "@chakra-ui/react";
import React from "react";

import { useNumberSearchParam } from "../../../hooks/useSearchParam";
import { useSendGAEvent } from "../../../utils/googleAnalytics";
import { CandidateRediscoveryProspect } from "../../graphql";
import ProspectCard from "./ProspectCard";
import ProspectEmailModal from "./ProspectEmailModal";

interface ProspectsAlphaProps {
  positionId: string;
  prospects: CandidateRediscoveryProspect[];
}

const ProspectsAlpha: React.FC<ProspectsAlphaProps> = ({
  positionId,
  prospects,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const sendGAEvent = useSendGAEvent();
  const [idx, setIdx] = useNumberSearchParam("idx", 0);
  const safeIdx = typeof idx === "number" && !Number.isNaN(idx) ? idx : 0;
  const selectedProspect = React.useMemo(() => {
    return Array.isArray(prospects) &&
      safeIdx >= 0 &&
      safeIdx < prospects.length
      ? prospects[safeIdx]
      : null;
  }, [prospects, safeIdx]);

  const openEmailModel = React.useCallback((): void => {
    if (selectedProspect) {
      onOpen();
      sendGAEvent("reconnect_candidate", "rediscovery");
    }
  }, [onOpen, selectedProspect, sendGAEvent]);

  return (
    <>
      <HStack alignItems="flex-start" spacing={4}>
        <Box
          bg="gray.50"
          borderRadius="12px"
          minWidth="280px"
          pt={5}
          pb={6}
          px={6}
        >
          <Text fontWeight="600" color="gray.800" mb={6}>
            Potential candidates &mdash; {prospects.length}
          </Text>
          <VStack spacing={0.5} alignItems="flex-start">
            {prospects?.map((prospect, index) => {
              const isSelected =
                selectedProspect?.candidateId === prospect.candidateId;
              return (
                <HStack
                  key={prospect.candidateId}
                  color={isSelected ? "blue.500" : "gray.900"}
                  fontSize="md"
                  spacing={0}
                  px={3}
                  py={1.5}
                  borderRadius="8px"
                  bg={isSelected ? "blackAlpha.50" : undefined}
                  _hover={{
                    bg: "blackAlpha.50",
                  }}
                  cursor="pointer"
                  width="100%"
                  fontWeight={isSelected ? "500" : "400"}
                  onClick={() => setIdx(index)}
                >
                  <Text
                    as="span"
                    color={isSelected ? "blue.500" : "gray.500"}
                    fontWeight="600"
                    width={8}
                    textAlign="center"
                  >
                    {index + 1}
                  </Text>
                  <Text>{prospect.candidate.fullName}</Text>
                </HStack>
              );
            })}
          </VStack>
        </Box>
        <VStack spacing={3}>
          {selectedProspect ? (
            <ProspectCard
              key={selectedProspect.candidateId}
              prospect={selectedProspect}
              index={prospects.findIndex(
                (p) => p.candidateId === selectedProspect.candidateId
              )}
              onOpenEmailModal={openEmailModel}
            />
          ) : (
            <Box color="gray.400" fontStyle="italic" p={8}>
              Select a candidate to view details
            </Box>
          )}
        </VStack>
      </HStack>
      <ProspectEmailModal
        isOpen={isOpen}
        onClose={onClose}
        prospect={selectedProspect}
        positionId={positionId}
      />
    </>
  );
};

export default ProspectsAlpha;
